import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Grid, Paper, Typography, Box, useTheme, alpha } from '@mui/material';
import { useDashboard } from '../../contexts/DashboardContext';
import { NavigationPolicyFromDashboard } from '../../utils/helper';
import TrendModal from '../../components/TrendModal';
import {
  People as PeopleIcon,
  ShoppingCart as CartIcon,
  AttachMoney as MoneyIcon,
  Payments as PaymentsIcon,
  AssignmentLate as OverdueIcon,
  AccessTime as TimeIcon,
  Event as EventIcon,
  CurrencyRupee as RupeeIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

// Define stat meta as an array for easier mapping and fetching
const statMeta = [
  {
    key: 'total_leads',
    title: 'Total Leads',
    icon: <PeopleIcon />, 
    color: '#e74c3c',
    stat_type: 'lead-stats',
  },
  {
    key: 'units_sold',
    title: 'Units Sold',
    icon: <CartIcon />,
    color: '#3498db',
    stat_type: 'units-sold',
  },
  {
    key: 'sales_revenue',
    title: 'Sales Revenue',
    icon: <RupeeIcon />,
    color: '#2ecc71',
    stat_type: 'sales-revenue',
  },
  {
    key: 'commission_revenue',
    title: 'Commission Revenue',
    icon: <PaymentsIcon />,
    color: '#9b59b6',
    stat_type: 'commission-revenue',
  },
  {
    key: 'commission_received',
    title: 'Commission Received',
    icon: <RupeeIcon />,
    color: '#f1c40f',
    stat_type: 'commission-received',
  },
  {
    key: 'overdue_commission',
    title: 'Overdue Commission',
    icon: <OverdueIcon />,
    color: '#e74c3c',
    stat_type: 'overdue-commission',
  },
  {
    key: 'lead_conversion_rate',
    title: 'Lead Conversion Rate',
    icon: <TrendingUpIcon />,
    color: '#e67e22',
    stat_type: 'lead-conversion-rate',
  },
  {
    key: 'revenue_per_unit_sold',
    title: 'Revenue Per Unit Sold',
    icon: <RupeeIcon />,
    color: '#95a5a6',
    stat_type: 'revenue-per-unit-sold',
  }
];

// Sample trend data for each metric
const sampleTrendData = {
  total_leads: [
    { date: '2025-10-01', value: 45 },
    { date: '2025-10-02', value: 52 },
    { date: '2025-10-03', value: 48 },
    { date: '2025-10-04', value: 61 },
    { date: '2025-10-05', value: 55 },
    { date: '2025-10-06', value: 67 },
    { date: '2025-10-07', value: 59 },
    { date: '2025-10-08', value: 73 },
    { date: '2025-10-09', value: 65 },
    { date: '2025-10-10', value: 78 },
    { date: '2025-10-11', value: 71 },
    { date: '2025-10-12', value: 84 },
    { date: '2025-10-13', value: 77 },
    { date: '2025-10-14', value: 89 },
    { date: '2025-10-15', value: 82 },
    { date: '2025-10-16', value: 95 },
    { date: '2025-10-17', value: 88 },
    { date: '2025-10-18', value: 101 },
    { date: '2025-10-19', value: 94 },
    { date: '2025-10-20', value: 107 },
    { date: '2025-10-21', value: 100 },
    { date: '2025-10-22', value: 113 },
    { date: '2025-10-23', value: 106 },
    { date: '2025-10-24', value: 119 },
  ],
  units_sold: [
    { date: '2025-10-01', value: 8 },
    { date: '2025-10-02', value: 12 },
    { date: '2025-10-03', value: 9 },
    { date: '2025-10-04', value: 15 },
    { date: '2025-10-05', value: 11 },
    { date: '2025-10-06', value: 18 },
    { date: '2025-10-07', value: 14 },
    { date: '2025-10-08', value: 21 },
    { date: '2025-10-09', value: 17 },
    { date: '2025-10-10', value: 24 },
    { date: '2025-10-11', value: 20 },
    { date: '2025-10-12', value: 27 },
    { date: '2025-10-13', value: 23 },
    { date: '2025-10-14', value: 30 },
    { date: '2025-10-15', value: 26 },
    { date: '2025-10-16', value: 33 },
    { date: '2025-10-17', value: 29 },
    { date: '2025-10-18', value: 36 },
    { date: '2025-10-19', value: 32 },
    { date: '2025-10-20', value: 39 },
    { date: '2025-10-21', value: 35 },
    { date: '2025-10-22', value: 42 },
    { date: '2025-10-23', value: 38 },
    { date: '2025-10-24', value: 45 },
  ],
  sales_revenue: [
    { date: '2025-10-01', value: 2400000 },
    { date: '2025-10-02', value: 3600000 },
    { date: '2025-10-03', value: 2700000 },
    { date: '2025-10-04', value: 4500000 },
    { date: '2025-10-05', value: 3300000 },
    { date: '2025-10-06', value: 5400000 },
    { date: '2025-10-07', value: 4200000 },
    { date: '2025-10-08', value: 6300000 },
    { date: '2025-10-09', value: 5100000 },
    { date: '2025-10-10', value: 7200000 },
    { date: '2025-10-11', value: 6000000 },
    { date: '2025-10-12', value: 8100000 },
    { date: '2025-10-13', value: 6900000 },
    { date: '2025-10-14', value: 9000000 },
    { date: '2025-10-15', value: 7800000 },
    { date: '2025-10-16', value: 9900000 },
    { date: '2025-10-17', value: 8700000 },
    { date: '2025-10-18', value: 10800000 },
    { date: '2025-10-19', value: 9600000 },
    { date: '2025-10-20', value: 11700000 },
    { date: '2025-10-21', value: 10500000 },
    { date: '2025-10-22', value: 12600000 },
    { date: '2025-10-23', value: 11400000 },
    { date: '2025-10-24', value: 13500000 },
  ],
  commission_revenue: [
    { date: '2025-10-01', value: 120000 },
    { date: '2025-10-02', value: 180000 },
    { date: '2025-10-03', value: 135000 },
    { date: '2025-10-04', value: 225000 },
    { date: '2025-10-05', value: 165000 },
    { date: '2025-10-06', value: 270000 },
    { date: '2025-10-07', value: 210000 },
    { date: '2025-10-08', value: 315000 },
    { date: '2025-10-09', value: 255000 },
    { date: '2025-10-10', value: 360000 },
    { date: '2025-10-11', value: 300000 },
    { date: '2025-10-12', value: 405000 },
    { date: '2025-10-13', value: 345000 },
    { date: '2025-10-14', value: 450000 },
    { date: '2025-10-15', value: 390000 },
    { date: '2025-10-16', value: 495000 },
    { date: '2025-10-17', value: 435000 },
    { date: '2025-10-18', value: 540000 },
    { date: '2025-10-19', value: 480000 },
    { date: '2025-10-20', value: 585000 },
    { date: '2025-10-21', value: 525000 },
    { date: '2025-10-22', value: 630000 },
    { date: '2025-10-23', value: 570000 },
    { date: '2025-10-24', value: 675000 },
  ],
  commission_received: [
    { date: '2025-10-01', value: 100000 },
    { date: '2025-10-02', value: 150000 },
    { date: '2025-10-03', value: 120000 },
    { date: '2025-10-04', value: 200000 },
    { date: '2025-10-05', value: 140000 },
    { date: '2025-10-06', value: 250000 },
    { date: '2025-10-07', value: 180000 },
    { date: '2025-10-08', value: 280000 },
    { date: '2025-10-09', value: 220000 },
    { date: '2025-10-10', value: 320000 },
    { date: '2025-10-11', value: 260000 },
    { date: '2025-10-12', value: 360000 },
    { date: '2025-10-13', value: 300000 },
    { date: '2025-10-14', value: 400000 },
    { date: '2025-10-15', value: 340000 },
    { date: '2025-10-16', value: 440000 },
    { date: '2025-10-17', value: 380000 },
    { date: '2025-10-18', value: 480000 },
    { date: '2025-10-19', value: 420000 },
    { date: '2025-10-20', value: 520000 },
    { date: '2025-10-21', value: 460000 },
    { date: '2025-10-22', value: 560000 },
    { date: '2025-10-23', value: 500000 },
    { date: '2025-10-24', value: 600000 },
  ],
  overdue_commission: [
    { date: '2025-10-01', value: 20000 },
    { date: '2025-10-02', value: 30000 },
    { date: '2025-10-03', value: 15000 },
    { date: '2025-10-04', value: 25000 },
    { date: '2025-10-05', value: 25000 },
    { date: '2025-10-06', value: 20000 },
    { date: '2025-10-07', value: 30000 },
    { date: '2025-10-08', value: 35000 },
    { date: '2025-10-09', value: 35000 },
    { date: '2025-10-10', value: 40000 },
    { date: '2025-10-11', value: 40000 },
    { date: '2025-10-12', value: 45000 },
    { date: '2025-10-13', value: 45000 },
    { date: '2025-10-14', value: 50000 },
    { date: '2025-10-15', value: 50000 },
    { date: '2025-10-16', value: 55000 },
    { date: '2025-10-17', value: 55000 },
    { date: '2025-10-18', value: 60000 },
    { date: '2025-10-19', value: 60000 },
    { date: '2025-10-20', value: 65000 },
    { date: '2025-10-21', value: 65000 },
    { date: '2025-10-22', value: 70000 },
    { date: '2025-10-23', value: 70000 },
    { date: '2025-10-24', value: 75000 },
  ],
  lead_conversion_rate: [
    { date: '2025-10-01', value: 18 },
    { date: '2025-10-02', value: 23 },
    { date: '2025-10-03', value: 19 },
    { date: '2025-10-04', value: 25 },
    { date: '2025-10-05', value: 20 },
    { date: '2025-10-06', value: 27 },
    { date: '2025-10-07', value: 24 },
    { date: '2025-10-08', value: 29 },
    { date: '2025-10-09', value: 26 },
    { date: '2025-10-10', value: 31 },
    { date: '2025-10-11', value: 28 },
    { date: '2025-10-12', value: 32 },
    { date: '2025-10-13', value: 30 },
    { date: '2025-10-14', value: 34 },
    { date: '2025-10-15', value: 32 },
    { date: '2025-10-16', value: 35 },
    { date: '2025-10-17', value: 33 },
    { date: '2025-10-18', value: 36 },
    { date: '2025-10-19', value: 34 },
    { date: '2025-10-20', value: 37 },
    { date: '2025-10-21', value: 35 },
    { date: '2025-10-22', value: 38 },
    { date: '2025-10-23', value: 36 },
    { date: '2025-10-24', value: 39 },
  ],
  revenue_per_unit_sold: [
    { date: '2025-10-01', value: 300000 },
    { date: '2025-10-02', value: 300000 },
    { date: '2025-10-03', value: 300000 },
    { date: '2025-10-04', value: 300000 },
    { date: '2025-10-05', value: 300000 },
    { date: '2025-10-06', value: 300000 },
    { date: '2025-10-07', value: 300000 },
    { date: '2025-10-08', value: 300000 },
    { date: '2025-10-09', value: 300000 },
    { date: '2025-10-10', value: 300000 },
    { date: '2025-10-11', value: 300000 },
    { date: '2025-10-12', value: 300000 },
    { date: '2025-10-13', value: 300000 },
    { date: '2025-10-14', value: 300000 },
    { date: '2025-10-15', value: 300000 },
    { date: '2025-10-16', value: 300000 },
    { date: '2025-10-17', value: 300000 },
    { date: '2025-10-18', value: 300000 },
    { date: '2025-10-19', value: 300000 },
    { date: '2025-10-20', value: 300000 },
    { date: '2025-10-21', value: 300000 },
    { date: '2025-10-22', value: 300000 },
    { date: '2025-10-23', value: 300000 },
    { date: '2025-10-24', value: 300000 },
  ],
};

// Helper function to format Indian currency values
const formatIndianCurrency = (value) => {
  if (value === null || value === undefined) return '--';
  
  const numValue = Number(value);
  if (isNaN(numValue)) return '--';
  
  if (numValue >= 10000000) {
    // Crores
    const croreValue = numValue / 10000000;
    return `₹${croreValue % 1 === 0 ? croreValue.toFixed(0) : croreValue.toFixed(2)}Cr`;
  } else if (numValue >= 100000) {
    // Lakhs
    const lakhValue = numValue / 100000;
    return `₹${lakhValue % 1 === 0 ? lakhValue.toFixed(0) : lakhValue.toFixed(2)}L`;
  } else if (numValue >= 1000) {
    // Thousands
    const thousandValue = numValue / 1000;
    return `₹${thousandValue % 1 === 0 ? thousandValue.toFixed(0) : thousandValue.toFixed(2)}K`;
  } else {
    // Less than 1000
    return `₹${numValue.toLocaleString()}`;
  }
};

// Helper function to format stat values
const formatStatValue = (key, value) => {
  if (value === null || value === undefined) return '--';
  
  switch (key) {
    case 'sales_revenue':
    case 'commission_revenue':
    case 'commission_received':
    case 'overdue_commission':
    case 'revenue_per_unit_sold':
      return formatIndianCurrency(value);
    case 'lead_conversion_rate':
      return `${value}%`;
    case 'total_leads':
    case 'units_sold':
      return value.toLocaleString();
    default:
      return value;
  }
};

const StatCard = ({ title, value, change, icon, color, trending, compareLabel, onClick }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
        background: 'background.paper',
        boxShadow: `0 2px 8px ${alpha(color, 0.08)}`,
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'default',
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
          transform: 'translateY(-2px)',
          border: `1px solid ${alpha(color, 0.4)}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: color,
              mb: 0.5,
              fontSize: '1.75rem',
            }}
          >
            {value}
          </Typography>
          {change && (
            <Typography
              variant="body2"
              sx={{
                color: change?.isPositive ? theme.palette.success.main : theme.palette.error.main,
                fontSize: '0.75rem',
                mt: 1,
              }}
            >
              {change.isPositive ? (
                <Box component="span" sx={{ mr: 0.5, fontSize: '0.875rem' }}>↑</Box>
              ) : (
                <Box component="span" sx={{ mr: 0.5, fontSize: '0.875rem' }}>↓</Box>
              )}
              {change.value}
              {compareLabel && (
                <span style={{ marginLeft: 6, color: theme.palette.text.secondary }}>
                  vs {compareLabel}
                </span>
              )}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

const StatsGrid = ({ dateRangeState, selectedTeams = [] }) => {
  const { fetchSalesPerformance } = useDashboard();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastFetchKey, setLastFetchKey] = useState('');
  
  // Trend modal state
  const [isTrendModalOpen, setIsTrendModalOpen] = useState(false);
  const [selectedTrendData, setSelectedTrendData] = useState(null);

  // Memoize the fetch function to prevent recreating it on every render
  const fetchAllStats = useCallback(async (currentStart, currentEnd, teamFilter = []) => {
    console.log('StatsGrid - fetchAllStats called with:', currentStart?.format('YYYY-MM-DD'), currentEnd?.format('YYYY-MM-DD'), 'teamFilter:', teamFilter);
    
    // Create a unique key for this fetch operation (include team filter)
    const teamFilterKey = teamFilter.length > 0 ? teamFilter.sort().join(',') : 'all';
    const fetchKey = `${currentStart?.format('YYYY-MM-DD')}_${currentEnd?.format('YYYY-MM-DD')}_${teamFilterKey}`;
    console.log('StatsGrid - fetchKey:', fetchKey, 'lastFetchKey:', lastFetchKey);
    
    // If we're already fetching this exact date range and team filter, don't fetch again
    if (fetchKey === lastFetchKey) {
      console.log('StatsGrid - Skipping fetch, already fetched this date range and team filter');
      return;
    }

    console.log('StatsGrid - Starting fetch...');
    setLoading(true);
    
    try {
      // Fetch from new sales performance endpoint with team filter
      const response = await fetchSalesPerformance(
        currentStart.format('YYYY-MM-DD'),
        currentEnd.format('YYYY-MM-DD'),
        teamFilter
      );
      
      console.log('StatsGrid - response:', response);
      if (response) {
        console.log('StatsGrid - API response:', response);
        // Map backend response to frontend expected format
        const statsObj = {};
        statMeta.forEach((meta) => {
          const statValue = response[meta.key];
          console.log(`StatsGrid - Processing ${meta.key}:`, statValue);
          if (statValue !== undefined) {
            statsObj[meta.key] = {
              value: statValue,
              change: null, // No comparison data for now
              trending: null
            };
          }
        });
        
        console.log('StatsGrid - Final statsObj:', statsObj);
        setStats(statsObj);
        setLastFetchKey(fetchKey);
      } else {
        console.log('StatsGrid - No response:', response);
        setStats({});
      }
    } catch (e) {
      console.error('Error fetching stats:', e);
      setStats({});
    } finally {
      setLoading(false);
    }
  }, [fetchSalesPerformance]);

  // Memoize the date range values and team filter to prevent unnecessary effect triggers
  const dateRange = useMemo(() => ({
    currentStart: dateRangeState?.currentStart,
    currentEnd: dateRangeState?.currentEnd
  }), [dateRangeState?.currentStart, dateRangeState?.currentEnd]);

  // Memoize team filter to prevent unnecessary effect triggers
  const teamFilter = useMemo(() => {
    return selectedTeams && selectedTeams.length > 0 ? selectedTeams : [];
  }, [selectedTeams]);

  useEffect(() => {
    if (!dateRange.currentStart || !dateRange.currentEnd) {
      return;
    }
    fetchAllStats(dateRange.currentStart, dateRange.currentEnd, teamFilter);
  }, [fetchAllStats, dateRange, teamFilter]);

  const handleCardClick = (meta) => {
    console.log('handleCardClick', meta);
    const cardType = meta.key;
    
    // Show trend modal for all cards
    const trendData = sampleTrendData[cardType];
    if (trendData) {
      setSelectedTrendData({
        title: `${meta.title} Trend`,
        data: trendData,
        dataKey: 'value',
        yAxisLabel: meta.title,
        color: meta.color,
        chartType: 'area'
      });
      setIsTrendModalOpen(true);
    }
  };

  const handleCloseTrendModal = () => {
    setIsTrendModalOpen(false);
    setSelectedTrendData(null);
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: 180, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // If no stats, create default stats with 0 values instead of showing "No data available"
  const displayStats = stats && Object.keys(stats).length > 0 ? stats : {};

  return (
    <>
      <Grid container spacing={3}>
        {statMeta.map((meta) => {
          const stat = displayStats[meta.key];
          console.log(`StatsGrid - Rendering ${meta.key}:`, stat);
          // Provide default values when no data is available
          const defaultStat = {
            value: '--',
            trending: 'stable',
            change: {
              value: '',
              label: '',
              // isPositive: false
            }
          };
          const finalStat = stat || defaultStat;
          console.log(`StatsGrid - Final stat for ${meta.key}:`, finalStat);
          
          return (
            <Grid item xs={12} sm={6} md={3} key={meta.key}>
              <StatCard
                title={meta.title}
                value={formatStatValue(meta.key, finalStat.value)}
                icon={meta.icon}
                color={meta.color}
                trending={finalStat.trending}
                change={finalStat.change}
                compareLabel={finalStat.change?.label}
              />
            </Grid>
          );
        })}
      </Grid>
      
      {/* Trend Modal */}
      <TrendModal
        open={isTrendModalOpen}
        onClose={handleCloseTrendModal}
        title={selectedTrendData?.title}
        data={selectedTrendData?.data}
        chartType={selectedTrendData?.chartType}
        dataKey={selectedTrendData?.dataKey}
        yAxisLabel={selectedTrendData?.yAxisLabel}
        color={selectedTrendData?.color}
      />
    </>
  );
};

export default React.memo(StatsGrid); 